package uk.co.nickthecoder.kyd.appearance

import uk.co.nickthecoder.glok.backend.backend
import uk.co.nickthecoder.glok.property.boilerplate.colorProperty
import uk.co.nickthecoder.glok.property.boilerplate.floatProperty
import uk.co.nickthecoder.glok.property.boilerplate.stringProperty
import uk.co.nickthecoder.glok.property.invalidationListener
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.util.Matrix
import uk.co.nickthecoder.kyd.Actor
import uk.co.nickthecoder.kyd.util.Vector2
import kotlin.math.ceil

class Label(

    val font: Font,
    text: String

) : TintedAppearance {


    override val tintProperty by colorProperty(Color.WHITE)
    override var tint by tintProperty


    val textProperty by stringProperty(text)
    var text by textProperty

    /**
     * 0 : Left aligned, 0.5 : Centered,  1 : Right aligned
     *
     * Default = 0.5 (centered)
     */
    val alignXProperty by floatProperty(0.5f)
    var alignX by alignXProperty

    /**
     * 0 : Align top, 0.5 : Centered,  1 : Aligned bottom
     *
     * Default = 0.5 (centered)
     */
    val alignYProperty by floatProperty(0.5f)
    var alignY by alignYProperty

    /**
     * Distance between lines of text as a ratio of the font's height.
     *
     * Default = 1.2
     */
    val lineHeightProperty by floatProperty(1.2f)
    var lineHeight by lineHeightProperty

    private var dirty = true

    private val dirtyListener = invalidationListener { dirty = true }

    init {
        for (prop in listOf(textProperty, alignXProperty, alignYProperty, lineHeightProperty)) {
            prop.addListener(dirtyListener)
        }
    }

    fun ensureClean() {
        if (dirty) {
            lines = text.split("\n")
            lineWidths = lines.map { font.bitmapFont.widthOf(it) }
            val maxLineWidth = lineWidths.max()
            val left = - maxLineWidth * alignX
            val right = left + maxLineWidth
            val totalHeight =
                lines.size * font.height * lineHeight + (lines.size - 1) * font.bitmapFont.height * (1 - lineHeight)
            val top = totalHeight * alignY
            val bottom = top - totalHeight

            corner0 = Vector2(left, top)
            corner2 = Vector2(right, bottom)
            corners = listOf(corner0, Vector2(right, top), corner2, Vector2(left, bottom))
        }
    }

    private var lines = listOf<String>()
    private var lineWidths = listOf<Float>()

    private var corner0 = Vector2.ZERO
    private var corner2 = Vector2.ZERO

    private var corners = listOf<Vector2>()

    override fun draw(modelMatrix: Matrix) {
        ensureClean()
        val bf = font.bitmapFont
        var y = corner0.y - font.bitmapFont.ascent
        for ((i, line) in lines.withIndex()) {
            var x = - lineWidths[i] * alignX

            backend.batch(font.bitmapFont.texture, tint, modelMatrix) {
                for (c in line) {
                    val glyphData = bf.glyphDataMap[c]
                    if (glyphData != null) {
                        draw(
                            glyphData.left, glyphData.top, glyphData.width, glyphData.height,
                            x - glyphData.boundsLeft, y + glyphData.boundsTop, glyphData.width, - glyphData.height,
                        )
                        // Move to the next position using advance, but keep pixel alignment.
                        x += ceil(glyphData.advance)
                    }
                }
            }

            y -= font.height * lineHeight
        }
    }

    override fun localCorners(): List<Vector2> {
        ensureClean()
        return corners
    }

    override fun localBounds(): Pair<Vector2, Vector2> {
        ensureClean()
        return Pair(corner0, corner2)
    }

    override fun containsLocalPoint(localPoint: Vector2): Boolean {
        ensureClean()

        if (localPoint.x < corner0.x) return false
        if (localPoint.x > corner2.x) return false
        if (localPoint.y > corner0.y) return false
        return localPoint.y >= corner2.y
    }

    override fun toString() = "Label font=${font.path()} text='$text'"
}
